import { BrowserRouter, Routes, Route, Outlet } from "react-router"
import { Suspense, lazy } from "react"
import { Toaster } from "sonner"
import { StoreCleanupProvider } from "@/components/StoreCleanupProvider"
import { DashboardLayout } from "@/layouts/DashboardLayout"

// Lazy-load all pages for code splitting
const HomePage = lazy(() => import("@/pages/HomePage"))
const TendersPage = lazy(() => import("@/pages/TendersPage"))
const TendersImportPage = lazy(() => import("@/pages/TendersImportPage"))
const ExpedientDetailPage = lazy(() => import("@/pages/ExpedientDetailPage"))
const DocumentEditorPage = lazy(() => import("@/pages/DocumentEditorPage"))
const ExpedientEditorPage = lazy(() => import("@/pages/ExpedientEditorPage"))
const ReportDetailPage = lazy(() => import("@/pages/ReportDetailPage"))
const TenderAssistantPage = lazy(() => import("@/pages/TenderAssistantPage"))
const TenderAssistantSessionPage = lazy(() => import("@/pages/TenderAssistantSessionPage"))
const ProcurementTemplatesPage = lazy(() => import("@/pages/ProcurementTemplatesPage"))
const TemplateImportPage = lazy(() => import("@/pages/TemplateImportPage"))
const ProcurementTemplateDetailPage = lazy(() => import("@/pages/ProcurementTemplateDetailPage"))
const PublicTendersPage = lazy(() => import("@/pages/PublicTendersPage"))
const PublicTenderDetailPage = lazy(() => import("@/pages/PublicTenderDetailPage"))
const OrganizationsPage = lazy(() => import("@/pages/OrganizationsPage"))
const OrganizationDetailPage = lazy(() => import("@/pages/OrganizationDetailPage"))
const NewEvaluationPage = lazy(() => import("@/pages/NewEvaluationPage"))
const EvaluationFromDocsPage = lazy(() => import("@/pages/EvaluationFromDocsPage"))
const EvaluatorPage = lazy(() => import("@/pages/EvaluatorPage"))
const EvaluatorHistoryPage = lazy(() => import("@/pages/EvaluatorHistoryPage"))
const EvaluatorHistoryDetailPage = lazy(() => import("@/pages/EvaluatorHistoryDetailPage"))
const BundleDetailPage = lazy(() => import("@/pages/BundleDetailPage"))
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"))
const TemplateDetailPage = lazy(() => import("@/pages/TemplateDetailPage"))
const TemplateEditPage = lazy(() => import("@/pages/TemplateEditPage"))
const NewTemplatePage = lazy(() => import("@/pages/NewTemplatePage"))
const TemplateGroupPage = lazy(() => import("@/pages/TemplateGroupPage"))
const AdminPage = lazy(() => import("@/pages/AdminPage"))
const BrandPage = lazy(() => import("@/pages/BrandPage"))
const SettingsPage = lazy(() => import("@/pages/SettingsPage"))
const RoleLabelsSettingsPage = lazy(() => import("@/pages/RoleLabelsSettingsPage"))
const OrganizationPage = lazy(() => import("@/pages/OrganizationPage"))
const AuditPage = lazy(() => import("@/pages/AuditPage"))
const SearchPage = lazy(() => import("@/pages/SearchPage"))
const TemplateDocumentPage = lazy(() => import("@/pages/TemplateDocumentPage"))
const TemplateEditorPage = lazy(() => import("@/pages/TemplateEditorPage"))
const SectionLibraryPage = lazy(() => import("@/pages/SectionLibraryPage"))
const IjToolPage = lazy(() => import("@/pages/IjToolPage"))
const NewPage = lazy(() => import("@/pages/NewPage"))
const LoginPage = lazy(() => import("@/pages/LoginPage"))
const CreateAdminPage = lazy(() => import("@/pages/CreateAdminPage"))
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"))

function PageLoader() {
	return (
		<div className='flex items-center justify-center h-full'>
			<div className='optimgov-loader'>
				<img src='/OptimGovLogo.png' alt='Loading' className='h-10 w-10' />
			</div>
		</div>
	)
}

export function App() {
	return (
		<StoreCleanupProvider>
			<BrowserRouter basename={(window as any).__CONTRACTACIO_CONFIG__?.basePath ?? ""}>
				<Suspense fallback={<PageLoader />}>
					<Routes>
						{/* Auth pages (no dashboard layout) */}
						<Route path='/login' element={<LoginPage />} />
						<Route path='/create-admin' element={<CreateAdminPage />} />
						<Route path='/verify-email' element={<VerifyEmailPage />} />

					{/* Dashboard pages (with sidebar + header) */}
						<Route
							element={
								<DashboardLayout>
									<Outlet />
								</DashboardLayout>
							}
						>
							<Route index element={<HomePage />} />
							<Route path='tenders' element={<TendersPage />} />
							<Route path='tenders/import' element={<TendersImportPage />} />
							<Route path='expedients/:id' element={<ExpedientDetailPage />} />
							<Route path='expedients/:id/editor' element={<ExpedientEditorPage />} />
							<Route path='expedients/:id/documents/:reportId' element={<DocumentEditorPage />} />
							<Route path='reports/:id' element={<ReportDetailPage />} />
							<Route path='tender-assistant' element={<TenderAssistantPage />} />
							<Route path='tender-assistant/:sessionId' element={<TenderAssistantSessionPage />} />
							<Route path='procurement-templates' element={<ProcurementTemplatesPage />} />
							<Route path='procurement-templates/import' element={<TemplateImportPage />} />
							<Route path='procurement-templates/:id' element={<ProcurementTemplateDetailPage />} />
							<Route path='procurement-templates/:id/editor' element={<TemplateEditorPage />} />
							<Route path='procurement-templates/:id/documents/:reportId' element={<TemplateDocumentPage />} />
							<Route path='public-tenders' element={<PublicTendersPage />} />
							<Route path='public-tenders/:id' element={<PublicTenderDetailPage />} />
							<Route path='public-tenders/organizations' element={<OrganizationsPage />} />
							<Route path='public-tenders/organizations/:id' element={<OrganizationDetailPage />} />
							<Route path='evaluations/new' element={<NewEvaluationPage />} />
							<Route path='evaluations/new/from-documents' element={<EvaluationFromDocsPage />} />
							<Route path='evaluator' element={<EvaluatorPage />} />
							<Route path='evaluator/history' element={<EvaluatorHistoryPage />} />
							<Route path='evaluator/history/:id' element={<EvaluatorHistoryDetailPage />} />
							<Route path='bundles/:id' element={<BundleDetailPage />} />
							<Route path='templates' element={<TemplatesPage />} />
							<Route path='templates/new' element={<NewTemplatePage />} />
							<Route path='templates/:id' element={<TemplateDetailPage />} />
							<Route path='templates/:id/edit' element={<TemplateEditPage />} />
							<Route path='templates/group/:groupId' element={<TemplateGroupPage />} />
							<Route path='admin' element={<AdminPage />} />
							<Route path='brand' element={<BrandPage />} />
							<Route path='settings' element={<SettingsPage />} />
							<Route path='settings/role-labels' element={<RoleLabelsSettingsPage />} />
							<Route path='organization' element={<OrganizationPage />} />
							<Route path='organization/users' element={<OrganizationPage />} />
							<Route path='organization/teams' element={<OrganizationPage />} />
							{/* Legacy redirects */}
							<Route path='users' element={<OrganizationPage />} />
							<Route path='teams' element={<OrganizationPage />} />
							<Route path='admin/audit' element={<AuditPage />} />
							<Route path='search' element={<SearchPage />} />
							<Route path='section-library' element={<SectionLibraryPage />} />
							<Route path='ij-tool' element={<IjToolPage />} />
							<Route path='new' element={<NewPage />} />
						</Route>
					</Routes>
				</Suspense>
			</BrowserRouter>
			<Toaster position='bottom-right' richColors />
		</StoreCleanupProvider>
	)
}
